import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Button, Col, Form, Input, Modal, Row, Spin, Tag, Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Moment from 'moment';
import Table from 'components/Table';
import { PersonaConPlaza } from 'components/SeguimientoDeRegistro/ListaDeSeguimientos';
import Uploader from 'components/Uploader';
import ModalDelete from 'components/ModalDelete';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import {
  deleteAdjuntoDeSeguimiento,
  getAdjuntosDeSeguimiento,
  patchAdjuntoDeSeguimiento,
  postAdjuntoDeSeguimiento,
} from 'api/configuracion/seguimientoDeRegistro';

const { Text } = Typography;

const rules = {
  required: [{ required: true, message: 'El campo es requerido' }],
};

const formatDate = (value) => (value ? Moment(value).format('DD/MM/YYYY') : '');

const permission = {
  add: true, change: true, delete: true, hasAllViewPermission: true,
};
const permissionCerrada = {
  add: false, change: false, delete: false, hasAllViewPermission: true,
};

const TIPOS_DE_VISTA = {
  pdf: { tipo: 'pdf', mime: 'application/pdf' },
  jpg: { tipo: 'img', mime: 'image/jpeg' },
  jpeg: { tipo: 'img', mime: 'image/jpeg' },
  png: { tipo: 'img', mime: 'image/png' },
};

const extensionDe = (nombre) => (nombre || '').split('?')[0].split('.').pop().toLowerCase();

// Grid de "Adjuntos" de una Nota de Seguimiento (evidencia) con visor del documento, igual
// que los anexos de Requisiciones Individuales en el SAC: al elegir un renglón se muestra el
// PDF (o imagen) al lado. Incluye los documentos que vienen dentro de los mensajes del hilo.
function AdjuntosDeSeguimiento({
  seguimientoId, cerrada, seleccionId, onSeleccion, refresco, onCambio,
}) {
  const token = useSelector(({ auth }) => auth.token);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [vista, setVista] = useState(null); // { url, tipo, nombre, origen }
  const [cargandoVista, setCargandoVista] = useState(false);
  const urlRef = useRef(null);

  const limpiarVista = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setVista(null);
  };

  const cargarDocumento = async (adjunto) => {
    limpiarVista();
    if (!adjunto?.documento) return;
    const nombre = adjunto.documento.split('/').pop().split('?')[0];
    const tipoDeVista = TIPOS_DE_VISTA[extensionDe(nombre)];
    if (!tipoDeVista) {
      setVista({
        url: null, tipo: 'otro', nombre, origen: adjunto.documento,
      });
      return;
    }
    try {
      setCargandoVista(true);
      const response = await fetch(adjunto.documento, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('No se pudo obtener el documento');
      const blob = new Blob([await response.blob()], { type: tipoDeVista.mime });
      urlRef.current = URL.createObjectURL(blob);
      setVista({
        url: urlRef.current, tipo: tipoDeVista.tipo, nombre, origen: adjunto.documento,
      });
    } catch (err) {
      setVista({
        url: null, tipo: 'error', nombre, origen: adjunto.documento,
      });
    } finally {
      setCargandoVista(false);
    }
  };

  const descargar = async () => {
    if (!vista?.origen) return;
    try {
      const response = await fetch(vista.origen, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('No se pudo obtener el documento');
      const url = URL.createObjectURL(await response.blob());
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = vista.nombre;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(err);
    }
  };

  const fetchData = async () => {
    if (!seguimientoId) {
      setData([]);
      return [];
    }
    setLoading(true);
    const adjuntos = (await getAdjuntosDeSeguimiento(seguimientoId))
      .map((e) => ({ ...e, key: e.id }));
    setData(adjuntos);
    setLoading(false);
    return adjuntos;
  };

  useEffect(() => {
    setSelectedRowKeys([]);
    limpiarVista();
  }, [seguimientoId]); // eslint-disable-line

  // Se vuelve a cargar cuando cambia la nota o cuando algo se agrega en la conversación.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [seguimientoId, refresco]);

  // Libera el documento abierto en el visor al cerrar la pestaña.
  useEffect(() => () => limpiarVista(), []); // eslint-disable-line

  // Selección que viene de fuera (p. ej. al dar clic en un adjunto dentro de la conversación).
  useEffect(() => {
    if (!seleccionId) return;
    const adjunto = data.find((e) => e.id === seleccionId);
    if (adjunto) {
      setSelectedRowKeys([adjunto.id]);
      cargarDocumento(adjunto);
    }
    // eslint-disable-next-line
  }, [seleccionId, data]);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    form.resetFields();
    setSelectedRowKeys([]);
    setVisible(true);
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const registro = data.find((e) => e.id === key);
    if (!registro) return;
    form.setFieldsValue(registro);
    setVisible(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchAdjuntoDeSeguimiento(key, values)
        : postAdjuntoDeSeguimiento({ ...values, seguimiento: seguimientoId, estados_globales: 1 }));
      if (saved) {
        onCancel();
        const adjuntos = await fetchData();
        onCambio?.();
        const guardado = adjuntos.find((e) => e.id === (key || saved.id));
        if (guardado) {
          setSelectedRowKeys([guardado.id]);
          cargarDocumento(guardado);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onDelete = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteAdjuntoDeSeguimiento(key);
    if (deleted) {
      setVisibleDelete(false);
      setSelectedRowKeys([]);
      limpiarVista();
      await fetchData();
      onCambio?.();
    }
    setLoading(false);
  };

  const onRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    onSeleccion?.(record.id);
    cargarDocumento(record);
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_de_captura',
      width: 110,
      render: formatDate,
    },
    {
      titleText: 'Usuario',
      dataIndex: 'usuario_empleado',
      width: 240,
      render: (value, record) => (
        <PersonaConPlaza
          nombre={value || record.usuario_nombre}
          plaza={record.usuario_puesto}
          area={record.usuario_area}
        />
      ),
    },
    {
      titleText: 'Rol',
      dataIndex: 'rol',
      width: 100,
      render: (value) => <Tag color={value === 'EMISOR' ? 'blue' : 'green'}>{value}</Tag>,
    },
    { titleText: 'Descripción', dataIndex: 'descripcion', width: 200 },
    { titleText: 'Estado', dataIndex: 'estados_globales_descripcion', width: 100 },
  ];

  if (!seguimientoId) {
    return (
      <Text type="secondary">
        Guarde la nota de seguimiento para agregar adjuntos.
      </Text>
    );
  }

  const selected = data.find((e) => e.id === selectedRowKeys[0]);

  const visor = () => {
    if (cargandoVista) {
      return <Spin tip="Cargando documento..." style={{ display: 'block', margin: '120px auto' }} />;
    }
    if (!vista) {
      return (
        <div style={{
          minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <Text type="secondary">Selecciona un documento para visualizarlo</Text>
        </div>
      );
    }
    if (vista.tipo === 'pdf') {
      return (
        <iframe
          src={vista.url}
          title={vista.nombre}
          style={{
            width: '100%', height: 'calc(100vh - 360px)', minHeight: 420, border: 'none',
          }}
        />
      );
    }
    if (vista.tipo === 'img') {
      return (
        <img
          src={vista.url}
          alt={vista.nombre}
          style={{
            maxWidth: '100%', maxHeight: 'calc(100vh - 360px)', display: 'block', margin: '0 auto',
          }}
        />
      );
    }
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Text type="secondary">
          {vista.tipo === 'error'
            ? 'No se pudo cargar la vista previa del documento.'
            : 'Este tipo de archivo no tiene vista previa.'}
        </Text>
      </div>
    );
  };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Row gutter={16}>
        <Col xs={24} xl={9}>
          <Table
            cols={columns}
            data={data}
            rowSelection={{ selectedRowKeys, type: 'radio' }}
            handleOnRowClick={onRowClick}
            controls={{ onClickAdd, onClickEdit, onClickDelete: () => setVisibleDelete(true) }}
            permission={cerrada ? permissionCerrada : permission}
            mobileColIndex={2}
          />
        </Col>
        <Col xs={24} xl={15}>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
            }}
            >
              <Text strong>{selected?.descripcion || 'Vista previa'}</Text>
              {vista?.origen && (
                <Button size="small" icon={<DownloadOutlined />} onClick={descargar}>
                  Descargar
                </Button>
              )}
            </div>
            {visor()}
          </div>
        </Col>
      </Row>
      <Modal
        open={visible}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Adjunto`}
            onFinish={onFinish}
            onCancel={onCancel}
            mutable
            loading={loading}
          />
        )}
        closable={false}
        maskClosable={!loading}
        footer={null}
        width={600}
        destroyOnClose
        forceRender
      >
        <Form layout="vertical" form={form} onFinish={onFinish} scrollToFirstError>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item name="descripcion" label="Descripción" rules={rules.required}>
                <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Uploader
              title="Documento"
              limitMB={5}
              file={selected?.documento}
              formItemName="documento"
              allowedExtensions={['pdf', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']}
              rules={selectedRowKeys.length ? undefined : rules.required}
              allowPreview={false}
              fullSize
            />
          </Row>
        </Form>
      </Modal>
      <ModalDelete
        open={visibleDelete}
        onDelete={onDelete}
        onCancel={() => setVisibleDelete(false)}
        content={`el adjunto ${selected?.descripcion || ''}`}
        loading={loading}
      />
    </Spin>
  );
}

AdjuntosDeSeguimiento.propTypes = {
  seguimientoId: PropTypes.number,
  cerrada: PropTypes.bool,
  seleccionId: PropTypes.number,
  onSeleccion: PropTypes.func,
  refresco: PropTypes.number,
  onCambio: PropTypes.func,
};

AdjuntosDeSeguimiento.defaultProps = {
  seguimientoId: null,
  cerrada: false,
  seleccionId: null,
  onSeleccion: null,
  refresco: 0,
  onCambio: null,
};

export default AdjuntosDeSeguimiento;
